import manropeCss from "@fontsource-variable/manrope?url";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { FormDevtoolsPanel } from "@tanstack/react-form-devtools";
import type { QueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
	Scripts,
	useRouter,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { tableDevtoolsPlugin } from "@tanstack/react-table-devtools";
import type { TFunction } from "i18next";
import { useEffect, useRef } from "react";
import { EnvironmentViewer } from "@/components/devtools/EnvironmentViewer";
import { NotFound } from "@/components/Organisms/NotFound";
import type { ConfigRouterContext } from "@/contexts/ConfigProvider";
import { useAppTranslation } from "@/hooks/use-app-translation";
import TanStackQueryDevtools from "../integrations/tanstack-query/devtools";
import "../styles/globals.css";
// Import React DevTools detection for proper DevTools functionality
import "../utils/react-devtools-detection";

interface MyRouterContext {
	queryClient: QueryClient;
	config: ConfigRouterContext;
	t: TFunction;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
			{
				title: "Fawkes Web",
			},
		],
		links: [
			{ rel: "stylesheet", href: manropeCss },
			// Light mode favicons (default and explicit)
			{
				rel: "icon",
				type: "image/png",
				sizes: "32x32",
				href: "https://opscdn.blob.core.windows.net/assets/lightBackground/Icon/OpSource-Icon-LB-FullColor.png",
				media: "(prefers-color-scheme: light)",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "16x16",
				href: "https://opscdn.blob.core.windows.net/assets/lightBackground/Icon/OpSource-Icon-LB-FullColor.png",
				media: "(prefers-color-scheme: light)",
			},
			// Dark mode favicons
			{
				rel: "icon",
				type: "image/png",
				sizes: "32x32",
				href: "https://opscdn.blob.core.windows.net/assets/darkBackground/Icon/OpSource-Icon-DB-FullColor.png",
				media: "(prefers-color-scheme: dark)",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "16x16",
				href: "https://opscdn.blob.core.windows.net/assets/darkBackground/Icon/OpSource-Icon-DB-FullColor.png",
				media: "(prefers-color-scheme: dark)",
			},
			// Default fallback for browsers that don't support media queries
			{
				rel: "icon",
				type: "image/png",
				sizes: "32x32",
				href: "https://opscdn.blob.core.windows.net/assets/lightBackground/Icon/OpSource-Icon-LB-FullColor.png",
			},
			// Apple touch icons for both modes
			{
				rel: "apple-touch-icon",
				sizes: "180x180",
				href: "https://opscdn.blob.core.windows.net/assets/lightBackground/Icon/OpSource-Icon-LB-FullColor.png",
				media: "(prefers-color-scheme: light)",
			},
			{
				rel: "apple-touch-icon",
				sizes: "180x180",
				href: "https://opscdn.blob.core.windows.net/assets/darkBackground/Icon/OpSource-Icon-DB-FullColor.png",
				media: "(prefers-color-scheme: dark)",
			},
			{
				rel: "manifest",
				href: "/manifest.json",
			},
		],
	}),

	shellComponent: RootDocument,
	component: RootComponent,
	notFoundComponent: NotFound,
});

/**
 * RootDocument - Shell component for the HTML document structure
 *
 * IMPORTANT: This is the shellComponent, which renders the HTML shell.
 * It can be invoked during route preloading BEFORE the RouterProvider context
 * is available. Therefore, we MUST NOT use useRouter() or other router hooks here.
 *
 * The language change handler is moved to RootComponent which is rendered
 * inside the RouterProvider context.
 */
function RootDocument({ children }: { children: React.ReactNode }) {
	const { i18n } = useAppTranslation();
	const isDev = import.meta.env.DEV;

	return (
		<html lang={i18n.language}>
			<head>
				<HeadContent />
			</head>
			<body>
				<main className="min-h-screen [view-transition-name:main-content]">
					{children}
				</main>
				<TanStackDevtools
					config={{
						position: "bottom-right",
						hideUntilHover: !isDev,
						openHotkey: [],
					}}
					plugins={[
						TanStackQueryDevtools,
						{
							name: "Tanstack Router",
							render: <TanStackRouterDevtoolsPanel />,
						},
						{
							name: "Tanstack Form",
							render: <FormDevtoolsPanel />,
						},
						tableDevtoolsPlugin(),
						{
							name: "Environment Viewer",
							render: <EnvironmentViewer />,
						},
					]}
				/>
				<Scripts />
			</body>
		</html>
	);
}

/**
 * RootComponent - Root route component rendered inside RouterProvider context
 *
 * This component handles the language change effect that invalidates the router
 * when the i18n language changes. It's safe to use useRouter() here because
 * this component is rendered as a route component, which is always inside
 * the RouterProvider context.
 */
function RootComponent() {
	const { i18n } = useAppTranslation();
	const router = useRouter();
	/** Only invalidate when the locale code actually changes — not on bundle-only updates (same emit from applyAuthenticatedTranslations). */
	const previousLngRef = useRef(i18n.language);

	useEffect(() => {
		const handler = (lng: string) => {
			const next = lng ?? i18n.language;
			const prev = previousLngRef.current;
			if (next !== prev) {
				router.invalidate();
			}
			previousLngRef.current = next;
		};
		i18n.on("languageChanged", handler);
		return () => {
			i18n.off("languageChanged", handler);
		};
	}, [router, i18n]);

	return <Outlet />;
}
